import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminPatrolSystem,
  InsertAdminPatrolSystemBody,
  UpdateRoofMaterialNewImageBody,
  UpdateRoofMaterialRemoveImageBody,
} from "./types/patrolSystems";

export const usePatrolSystemsApi = () => {
  const api = useApi();

  const getAdminPatrolSystem = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/systems`;
      return useSWRMutation<AdminPatrolSystem, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminPatrolSystem>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminPatrolSystems = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/systems", query);
      return useSWR<Paginated<AdminPatrolSystem>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminPatrolSystem>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminPatrolSystem = useCallback(
    async (body: InsertAdminPatrolSystemBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/systems`;
      const id = await api.post<{ id: string }, InsertAdminPatrolSystemBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminPatrolSystem = useCallback(
    async (
      patrolSystemId: string,
      body: UpdateRoofMaterialRemoveImageBody | UpdateRoofMaterialNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${patrolSystemId}`;
      await api.put<UpdateRoofMaterialRemoveImageBody | UpdateRoofMaterialNewImageBody>(url, body);
      //mutateCache(["/admin/settings/patrol/systems"], url);
    },
    [api],
  );

  const deleteAdminPatrolSystem = useCallback(
    async (patrolSystemId: string): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${patrolSystemId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/systems`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (patrolSystemId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/systems/${patrolSystemId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminPatrolSystemVisibility = useCallback(
    async (patrolSystemId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${patrolSystemId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/systems"]);
    },
    [api],
  );

  const updateAdminPatrolSystemOrder = useCallback(
    async (patrolSystemId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${patrolSystemId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/systems"]);
    },
    [api],
  );

  return {
    getAdminPatrolSystem,
    useAdminPatrolSystems,
    insertAdminPatrolSystem,
    updateAdminPatrolSystem,
    updateAdminPatrolSystemVisibility,
    updateAdminPatrolSystemOrder,
    deleteAdminPatrolSystem,
    generateUploadImageUrl,
  };
};
